import { useContext, useEffect, useState } from 'react';
import { Box, Button, Grid, IconButton, Typography, useTheme } from '@mui/material';
import MainButton from '../UI/MainButton';
import HideInputIcon from '../UI/HideInputIcon';
import isEmail from 'validator/lib/isEmail';
import SnackbarContext from '../../store/snackbar-context';
import AuthContext from '../../store/auth-context';
import { resendConfirmRegistrationEmail, sendResetLinkRequest } from '../../api/auth-api';
import FormsLayout from '../../layout/FormsLayout';
import { registrationPage } from '../../store/fixed-routing';
import DividerWithButton from '../UI/DividerWithButton';
import FormTextField from '../UI/FormTextField';
import { useCustomTheme } from '../../store/theme-context';
import { debugError } from '../../utils/debug';
import { useNavigate, useSearchParams } from 'react-router-dom';

const formErrors = {
	emailError: 'Wprowadź poprawny email',
	passwordError: 'Wprowadź hasło',
};

const LoginForm = () => {
	const { isHighContrast } = useCustomTheme();
	const { palette, typography, breakpoints } = useTheme();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [showPassword, setShowPassword] = useState(false);
	const { login, role } = useContext(AuthContext);
	const [passwordReset, setPasswordReset] = useState(searchParams.get('reset') === 'true');
	const [validated, setValidated] = useState(false);
	const [linkSent, setLinkSent] = useState(false);
	const [needsEmailConfirm, setNeedsEmailConfirm] = useState(false);
	const [resendBusy, setResendBusy] = useState(false);
	const { setMsg } = useContext(SnackbarContext);
	const [formInfo, setFormInfo] = useState({
		email: '',
		password: '',
	});

	const [formValidation, setFormValidation] = useState(formErrors);

	// Automatyczne przekierowanie po wysłaniu linku (po 3 sekundach)
	useEffect(() => {
		if (linkSent) {
			const timer = setTimeout(() => {
				navigate('/');
			}, 3000);
			return () => clearTimeout(timer);
		}
	}, [linkSent, navigate]);

	const emailHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				emailError: formErrors.emailError,
			});
		} else if (!isEmail(value)) {
			setFormValidation({
				...formValidation,
				emailError: 'Niepoprawny adres e-mail',
			});
		} else {
			setFormValidation({
				...formValidation,
				emailError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				email: value,
			};
		});
	};

	const passwordHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				passwordError: formErrors.passwordError,
			});
		} else {
			setFormValidation({
				...formValidation,
				passwordError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				password: value,
			};
		});
	};

	const actionSend = (event: { preventDefault: () => void }) => {
		setValidated(true);
		event.preventDefault();
		if (passwordReset) {
			if (formValidation.emailError !== '') {
				setMsg({ msg: formValidation.emailError, severity: 'warning' });
				return;
			}
			sendResetLinkRequest(formInfo.email, (error) => debugError('Reset password error:', error))
				.then((res) => {
					if (res && res.user_exists === false) {
						// Email nie istnieje w bazie
						setMsg({
							msg:
								res.message ||
								'Podany adres email nie jest zarejestrowany w systemie. Sprawdź poprawność adresu lub zarejestruj się.',
							severity: 'error',
						});
					} else if (res && res.user_exists === true) {
						// Email istnieje, link wysłany
						setLinkSent(true);
						setMsg({ msg: res.message || 'Sprawdź swoją skrzynkę pocztową', severity: 'info' });
					} else {
						// Domyślny komunikat (dla kompatybilności wstecznej)
						setLinkSent(true);
						setMsg({ msg: 'Sprawdź swoją skrzynkę pocztową', severity: 'info' });
					}
				})
				.catch((error) => {
					debugError('Reset password error:', error);
					setMsg({ msg: 'Wystąpił błąd przy resetowaniu hasła. Spróbuj ponownie później.', severity: 'error' });
				});
		} else {
			if (formValidation.emailError !== '') {
				setMsg({ msg: formValidation.emailError, severity: 'warning' });
				return;
			}
			if (formValidation.passwordError !== '') {
				setMsg({ msg: formValidation.passwordError, severity: 'warning' });
				return;
			}
			login(formInfo.email, formInfo.password, (_msg, code) => {
				setNeedsEmailConfirm(code === 'olijp_email_not_verified');
			});
		}
	};

	const resendConfirmEmail = () => {
		if (formValidation.emailError !== '' || !formInfo.email) {
			setMsg({ msg: formErrors.emailError, severity: 'warning' });
			return;
		}
		setResendBusy(true);
		resendConfirmRegistrationEmail(formInfo.email)
			.then((res) => {
				setMsg({
					msg: res?.message || 'Jeśli konto wymaga potwierdzenia, wysłaliśmy nowy link.',
					severity: res?.alreadyVerified ? 'info' : 'success',
				});
			})
			.catch((err) => {
				debugError('Resend confirm email failed:', err);
				setMsg({
					msg: err?.response?.data?.message || 'Nie udało się wysłać linku. Spróbuj ponownie później.',
					severity: 'error',
				});
			})
			.finally(() => setResendBusy(false));
	};

	return (
		<FormsLayout>
			<Grid
				width={'100%'}
				maxWidth={'650px'}
				display={'flex'}
				flexDirection={'column'}
				gap={'24px'}
			>
				<Typography
					variant={'h3'}
					fontWeight={'500'}
					color={palette.primary.main}
				>
					{!passwordReset ? 'Zaloguj się' : 'Resetuj/ustal hasło'}
				</Typography>
				<Grid
					component={'form'}
					autoComplete={'off'}
					display={'flex'}
					flexDirection={'column'}
				>
					<Box
						display={'flex'}
						flexDirection={'column'}
						alignItems={'flex-start'}
						gap={'4px'}
						width={'100%'}
						mb={'16px'}
					>
						<FormTextField
							id={'email'}
							type={'email'}
							label={'E-mail'}
							validated={validated}
							errorMsg={formValidation.emailError}
							value={formInfo.email}
							valueChangeHandler={emailHandler}
						/>
					</Box>
					{!passwordReset && (
						<FormTextField
							id={'password'}
							type={!showPassword ? 'password' : ''}
							label={'Hasło'}
							validated={validated}
							errorMsg={formValidation.passwordError}
							value={formInfo.password}
							valueChangeHandler={passwordHandler}
							endIconElement={
								<IconButton
									sx={{
										padding: 0,
										'& svg': {
											minHeight: '30px',
											minWidth: '30px',
										},
									}}
									onClick={() => {
										setShowPassword(!showPassword);
									}}
								>
									<HideInputIcon isHighContrast={isHighContrast} />
								</IconButton>
							}
						/>
					)}
					{/* Pokaż "Zaloguj się" tylko gdy nie wpisujemy emaila i nie wysłano linku */}
					{passwordReset && formInfo.email.length === 0 && !linkSent && (
						<Button
							sx={{
								fontSize: typography.h6.fontSize,
								fontWeight: '400',
								textDecoration: 'underline',
								textTransform: 'none',
								alignSelf: 'flex-end',
								color: palette.primary.main,
								cursor: 'pointer',
								mt: '4px',
								mb: '32px',
								[breakpoints.down('md')]: {
									fontSize: `${typography.h5.fontSize}`,
								},
							}}
							onClick={() => {
								setPasswordReset(false);
								setFormInfo({ email: '', password: '' });
							}}
						>
							Zaloguj się
						</Button>
					)}
					{!passwordReset && needsEmailConfirm && (
						<Box
							sx={{
								mb: 2,
								p: 2,
								borderRadius: 1,
								border: `1px solid ${palette.warning.main}`,
								backgroundColor: isHighContrast ? 'transparent' : 'rgba(237, 108, 2, 0.06)',
							}}
						>
							<Typography
								variant='body2'
								sx={{ mb: 1 }}
							>
								Konto jest nieaktywne — potwierdź adres e-mail linkiem z wiadomości po rejestracji.
							</Typography>
							<Button
								id='resend-confirm-email'
								variant='outlined'
								disabled={resendBusy}
								onClick={resendConfirmEmail}
								sx={{ textTransform: 'none' }}
							>
								{resendBusy ? 'Wysyłanie…' : 'Wyślij ponownie link potwierdzający'}
							</Button>
						</Box>
					)}
					{!passwordReset && (
						<Button
							sx={{
								fontSize: typography.h6.fontSize,
								fontWeight: '400',
								textDecoration: 'underline',
								textTransform: 'none',
								alignSelf: 'flex-end',
								color: palette.primary.main,
								cursor: 'pointer',
								mt: '4px',
								mb: '32px',
								[breakpoints.down('md')]: {
									fontSize: `${typography.h5.fontSize}`,
								},
							}}
							onClick={() => {
								setPasswordReset(true);
								setNeedsEmailConfirm(false);
							}}
						>
							Nie pamiętasz hasła?
						</Button>
					)}
					{!linkSent && (
						<Box
							alignSelf={'center'}
							mt={!passwordReset ? '0' : '16px'}
							onClick={actionSend}
						>
							<MainButton
								title={!passwordReset ? 'Zaloguj się' : 'Wyślij'}
								darkVersion={true}
								sx={{
									alignSelf: 'center',
									lineHeight: '20px',
									letterSpacing: '0.18px',
								}}
							/>
						</Box>
					)}
					{linkSent && (
						<Typography
							variant={'body1'}
							color={palette.text.secondary}
							sx={{ mt: 2, mb: 3, textAlign: 'center' }}
						>
							Link resetujący hasło został wysłany. Sprawdź swoją skrzynkę pocztową i kliknij w link, aby ustawić nowe
							hasło.
						</Typography>
					)}
				</Grid>
			</Grid>
			<DividerWithButton
				label={'Nie masz konta?'}
				hrefPath={registrationPage}
				buttonTitle={'Zarejestruj się'}
			/>
		</FormsLayout>
	);
};

export default LoginForm;
